import React, { MouseEvent, useMemo } from "react";
import styles from "./sectionContent.module.scss";
import { Content, ContentType, Lesson } from "src/types";
import IconPDF from "../../../../../../assets/icons/content_type_pdf.svg?react";
import IconAudio from "../../../../../../assets/icons/content_type_audio.svg?react";
import IconSlide from "../../../../../../assets/icons/content_type_slide.svg?react";
import SlideIcon from "@assets/icons/content_type_slide_com.svg?react";
import IconVideo from "../../../../../../assets/icons/content_type_video.svg?react";
import IconFeedback from "../../../../../../assets/icons/content_type_feedback.svg?react";
import IconMenu from "../../../../../../assets/icons/menu.svg?react";
import { useEditUnit } from "../../../edit/useEditUnit";
import type { DraggableProvided, DraggableStateSnapshot } from '@src/utils/componentCasts';
import { DragDropContext, Droppable, Draggable } from '@src/utils/componentCasts';

type Props = {
  content: Content;
  lesson?: Lesson;
  selected?: boolean;
  draggableProvided?: DraggableProvided;
  snapshot?: DraggableStateSnapshot;
  isPreparation?: boolean;
  isSupportingMaterial?: boolean;
};

const getContentIcon = (type: ContentType) => {
  switch (type) {
    case ContentType.PDF: {
      return IconPDF;
    }
    case ContentType.AUDIO: {
      return IconAudio;
    }
    // case ContentType.GOOGLESLIDE:
    case ContentType.SLIDE: {
      return IconSlide;
    }
    case ContentType.REVEALSLIDE: {
      return SlideIcon;
    }
    case ContentType.ZIPSLIDE: {
      return SlideIcon;
    }
    case ContentType.VIDEO: {
      return IconVideo;
    }
    case ContentType.FORM: {
      return IconFeedback;
    }
    default: {
      return null;
    }
  }
};

function SectionContent(props: Props) {
  const {
    draggableProvided,
    snapshot,
    content,
    selected,
    lesson,
    isPreparation,
    isSupportingMaterial,
  } = props;
  const { name } = content;

  const { setContent, setLesson, setSelectionLevel, setIsPreparation } =
    useEditUnit();

  const Icon = getContentIcon(content.type);

  const handleSelect = (e: MouseEvent<HTMLDivElement>) => {
    e.stopPropagation();
    setLesson(lesson);
    setContent(content);
    setSelectionLevel("Content");
    setIsPreparation(isPreparation);
  };

  const finalIndex =
    lesson?.modules?.findIndex((module) => module.additionalAfter === true) ??
    -1;
  const currentIndex =
    lesson?.modules?.findIndex((module) => module.id === content.id) ?? -1;
  const finalContent = finalIndex > -1 && currentIndex === finalIndex;
  const additionalContent =
    finalIndex > -1 &&
    currentIndex > finalIndex &&
    content?.type !== ContentType.FORM &&
    !content?.name.toLowerCase().includes("exit ticket");
  const completeContent = content.lastInLesson === true;
  const wrapperClassName = useMemo(() => {
    const classes = [styles.wrapper];
    if (selected) {
      classes.push(styles.selected);
    }
    if (snapshot?.isDragging) {
      classes.push(styles.moving);
    }
    return classes.join(" ");
  }, [selected, snapshot?.isDragging]);

  return (
    <div
      className={wrapperClassName}
      onClick={handleSelect}
      {...draggableProvided?.dragHandleProps}
    >
      <div
        className={styles.menu}
        style={{
          display: snapshot?.isDragging ? "flex" : "",
        }}
      >
        <IconMenu />
      </div>
      <div className={isSupportingMaterial ? styles.smCircle : styles.circle}>
        {isSupportingMaterial ? "SM" : ""}
      </div>
      <div className={styles.title}>
        {name}
        {finalContent ? (
          " (F)"
        ) : additionalContent ? (
          <strong>{completeContent ? " (C)" : " (A)"}</strong>
        ) : (
          <strong>{completeContent ? " (C)" : ""}</strong>
        )}
      </div>
      <div className={styles.meta}></div>
      <div className={styles.typeIconWrapper}>
        {content.type === ContentType.PARENT ? (
          <div className={styles.paColor}>PS</div>
        ) : (
          Icon && <Icon className={styles.typeIcon} />
        )}
      </div>
    </div>
  );
}

export default React.memo(SectionContent);
